import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { makeStyles } from "@mui/styles";
import { Box, Typography } from "@mui/material";
import MainActionBar from "./MainContent/ActionBar/MainActionBar";
import { selectSubmission } from "../../../redux/tasksSlice";
import { useQuery } from "../../../hooks";
import {
  selectCurrentInteraction,
  setSelectedInteractionId,
  selectedQuestionHighlights
} from "../../../redux/interactionsSlice";
import { VIEWS } from "./consts";
import BookView from "../../reader/BookView";
import { HIGHLIGHTS } from "../../../consts";
import CardsView from "../../SharedComponents/cards/CardsView";
import ReviewEvidenceToolTip from "../../SharedComponents/ReviewEvidenceToolTip";
import { useIntl } from "react-intl";
import AnswerView from "./MainContent/AnswerView";

const useStyles = makeStyles((theme) => ({
  container: {
    flex: 1,
    height: "100%",
    backgroundColor: theme.palette.grey.paper,
    display: "flex",
    flexDirection: "column",
    borderRadius: "10px",
    overflow: "hidden"
  },
  readerView: {
    position: "relative",
    flex: 1,
    height: "100%",
    display: "flex",
    flexDirection: "column",
    justifyContent: "flex-start",
    overflow: "auto"
  },
  content: {
    width: "100%",
    flex: 1,
    justifyContent: "center",
    display: "flex"
  },
  cardsList: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    width: "100%",
    height: "100%"
  },
  stageHeaderContent: {
    display: "flex",
    flexDirection: "row",
    maxWidth: 700,
    width: "66%",
    justifyContent: "center"
  },
  // Combined view specific styles
  combinedContainer: {
    display: "flex",
    width: "100%",
    height: "100%"
  },
  combinedQuestion: {
    padding: theme.spacing(2),
    borderBottom: `1px solid ${theme.palette.divider}`
  },
  combinedAnswer: {
    flex: 1,
    padding: theme.spacing(2),
    overflow: "auto"
  }
}));

const ContentPanel = ({
  activeMode,
  handleModeChange,
  combinedViewMode,
  setCombinedViewMode
}) => {
  const classes = useStyles();
  const intl = useIntl();
  const { submission_id } = useQuery();
  const dispatch = useDispatch();
  const [contentWidth, setContentWidth] = useState(800);

  // Selectors
  const submission = useSelector((state) =>
    selectSubmission(state, Number(submission_id))
  );
  const questionHighlights = useSelector(selectedQuestionHighlights);
  const questions = useSelector((state) => state.interactions.questions);
  const selectedQuestion = useSelector(selectCurrentInteraction);

  // Derived state
  const isSubmitted = submission.status === "Submitted";
  const containerRef = useRef(null);

  // Handle resize observation
  useEffect(() => {
    if (!containerRef.current) return;

    const resizeObserver = new ResizeObserver((entries) => {
      if (entries[0]) {
        const { width } = entries[0].contentRect;
        setContentWidth(Math.min(width, 1000)); // Cap at 1000px
      }
    });

    resizeObserver.observe(containerRef.current);
    return () => resizeObserver.disconnect();
  }, []);

  // Handle question selection and combined view
  useEffect(() => {
    // Set first question if none selected
    if (!selectedQuestion?.id && questions.length > 0) {
      dispatch(setSelectedInteractionId(questions[0].id));
    }

    // Update mode when combined view changes
    if (combinedViewMode) {
      handleModeChange(VIEWS.COMBINED);
    }
  }, [
    selectedQuestion,
    questions,
    combinedViewMode,
    dispatch,
    handleModeChange
  ]);

  // Combined view rendering
  const renderCombinedView = () => (
    <Box className={classes.combinedContainer}>
      {/* Left side - BookView */}
      <BookView
        readOnly={isSubmitted}
        minimalBar={false}
        disableInteractions={isSubmitted}
        isVisible={true}
      />

      {/* Right side - Question & Answer */}
    </Box>
  );

  // Regular view rendering
  const renderStandardContent = () => {
    switch (activeMode) {
      case VIEWS.READER:
        return (
          <BookView
            readOnly={isSubmitted}
            minimalBar={false}
            disableInteractions={isSubmitted}
            isVisible={true}
          />
        );

      case VIEWS.ANSWERS:
        return <AnswerView question={selectedQuestion} width={contentWidth} />;

      case VIEWS.HIGHLIGHTS:
        return (
          <Box className={classes.cardsList}>
            {questionHighlights.length > 0 && (
              <Box className={classes.stageHeaderContent}>
                <Typography variant="h6" sx={{ lineHeight: "40px" }}>
                  {intl.formatMessage({
                    id: "task.stage.ReviewSupportingEvidence",
                    defaultMessage: "Review supporting evidence"
                  })}
                </Typography>
                {!isSubmitted && <ReviewEvidenceToolTip />}
              </Box>
            )}
            <CardsView
              color="secondary"
              context={HIGHLIGHTS.CONTEXT.ANSWERS}
              cards={questionHighlights}
              disable={isSubmitted}
            />
          </Box>
        );

      default:
        return null;
    }
  };

  // Content styles based on active mode
  const getContentStyles = () => {
    if (activeMode === VIEWS.COMBINED) {
      return {
        overflow: "hidden",
        padding: "0" // No padding for combined view to maximize space
      };
    } else if (activeMode === VIEWS.CARDS) {
      return {
        overflow: "auto"
      };
    } else {
      return {
        overflow: "hidden",
        padding: "32px"
      };
    }
  };

  return (
    <Box className={classes.container} ref={containerRef}>
      <Box className={classes.readerView}>
        <MainActionBar
          contentWidth={contentWidth}
          activeMode={activeMode}
          handleModeChange={handleModeChange}
          combinedViewMode={combinedViewMode}
          setCombinedViewMode={setCombinedViewMode}
        />

        <Box className={classes.content} sx={getContentStyles()}>
          {activeMode === VIEWS.COMBINED
            ? renderCombinedView()
            : renderStandardContent()}
        </Box>
      </Box>
    </Box>
  );
};

export default ContentPanel;
